import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame } from "lucide-react";
import api from "@/lib/api";
import { useLang, pickLocalized } from "@/contexts/LangContext";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const { t, lang } = useLang();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data || []));
  }, []);

  return (
    <div className="fade-in" data-testid="home-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-24 md:pb-32 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <p className="tracking-eyebrow text-[var(--eafg-copper)] flex items-center gap-2">
            <Flame size={14} strokeWidth={1.5} /> {t("home.eyebrow")}
          </p>
          <h1
            className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-6 tracking-tight"
            data-testid="home-hero-title"
          >
            {lang === "es" ? (
              <>
                Joyería <em className="not-italic text-[var(--eafg-copper)]">esmaltada</em>
                <br />
                al fuego, pieza
                <br />
                a pieza.
              </>
            ) : (
              <>
                Fired-enamel
                <br />
                <em className="not-italic text-[var(--eafg-copper)]">jewelry</em>, piece
                <br />
                by piece.
              </>
            )}
          </h1>
          <p className="text-base md:text-lg text-[var(--eafg-muted)] leading-relaxed mt-8 max-w-md">
            {t("home.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/tienda"
              className="inline-flex items-center gap-2 bg-[var(--eafg-ink)] text-white px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-copper)] transition-colors"
              data-testid="home-cta-shop"
            >
              {t("home.ctaShop")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 border border-[var(--eafg-ink)] px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-ink)] hover:text-white transition-colors"
              data-testid="home-cta-story"
            >
              {t("home.ctaStory")}
            </Link>
          </div>
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/4354613/pexels-photo-4354613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=950&w=940"
              alt="Taller artesanal de esmalte al fuego"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Categories — bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32" data-testid="home-categories">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("home.categoriesTitle")}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-2">
              {lang === "es" ? "Tres lenguajes." : "Three languages."}
              <span className="italic text-[var(--eafg-copper)]"> {lang === "es" ? "Un fuego." : "One fire."}</span>
            </h2>
          </div>
          <Link
            to="/tienda"
            className="hidden md:inline-flex items-center gap-2 tracking-eyebrow hover:text-[var(--eafg-copper)]"
          >
            {t("home.ctaShop")} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {categories.map((cat, idx) => {
            const span =
              idx === 0 ? "md:col-span-7 md:row-span-2" : idx === 1 ? "md:col-span-5" : "md:col-span-5";
            const aspect = idx === 0 ? "aspect-[5/6]" : "aspect-[5/4]";
            return (
              <Link
                key={cat.id}
                to={`/tienda/${cat.slug}`}
                className={`group ${span} relative overflow-hidden`}
                data-testid={`home-category-${cat.slug}`}
              >
                <div className={`${aspect} overflow-hidden bg-[#efece8]`}>
                  {cat.image_url && (
                    <img
                      src={cat.image_url}
                      alt={pickLocalized(cat, lang, "name")}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/60 to-transparent text-white">
                  <h3 className="font-serif-display text-3xl md:text-4xl">
                    {pickLocalized(cat, lang, "name")}
                  </h3>
                  <p className="tracking-eyebrow text-white/80 mt-2 inline-flex items-center gap-2">
                    {lang === "es" ? "Ver" : "View"} <ArrowRight size={12} strokeWidth={1.5} />
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32" data-testid="home-featured">
          <div className="mb-10">
            <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("home.featuredTitle")}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-2">
              {lang === "es" ? "Selección del taller" : "Workshop selection"}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-14 gap-x-8">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* About */}
      <section className="border-t border-[var(--eafg-line)]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <p className="tracking-eyebrow text-[var(--eafg-copper)]">{t("home.aboutTitle")}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-6 leading-tight">
            {lang === "es"
              ? "El vidrio se vuelve color. El cobre se vuelve memoria."
              : "Glass turns into color. Copper turns into memory."}
          </h2>
          <p className="text-[var(--eafg-muted)] mt-8 leading-relaxed">{t("home.aboutText")}</p>
        </div>
      </section>
    </div>
  );
}
